import { useState } from 'react';
import { AppBar, Toolbar, Button, IconButton, Stack, Box, useScrollTrigger, useTheme, useColorScheme, Typography, Menu, MenuItem, Collapse, List, ListItemButton, ListItemText, Divider, Drawer } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import LanguageIcon from '@mui/icons-material/Language';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from './Logo';

const GlassAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(15, 23, 42, 0.8)' 
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 30px rgba(0, 0, 0, 0.3)'
    : '0 4px 30px rgba(0, 0, 0, 0.1)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));


const NavButton = styled(Button)<{ component?: typeof Link; to?: string; active?: string }>(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: '1rem',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  textDecoration: 'none',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)',
  },
}));

const ContactButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: '0.9rem',
  padding: '8px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  border: `1.5px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.02)',
  backdropFilter: 'blur(10px)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}, transparent)`,
    transition: 'left 0.5s ease',
  },
  '&:hover': {
    border: `1.5px solid ${theme.palette.secondary.main}`,
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)',
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0, 0, 0, 0.2)'
      : '0 4px 12px rgba(0, 0, 0, 0.08)',
    '&::before': {
      left: '100%',
    },
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(15, 23, 42, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '12px',
    marginTop: '8px',
    minWidth: '200px',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 8px 32px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 20px',
  borderRadius: '8px',
  margin: '4px 8px',
  fontSize: '1rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
  },
}));

export default function Header() {
  const { language, setLanguage, t } = useLanguage();
  const { mode, setMode } = useColorScheme();
  const theme = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutAnchorEl, setAboutAnchorEl] = useState<null | HTMLElement>(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
  const [productsAnchorEl, setProductsAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  
  const logoVariant = mode === 'dark' ? 'white' : 'colored';
  const aboutMenuOpen = Boolean(aboutAnchorEl);
  const servicesMenuOpen = Boolean(servicesAnchorEl);
  const productsMenuOpen = Boolean(productsAnchorEl);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const toggleLanguage = () => {
    setLanguage(language === 'tr' ? 'en' : 'tr');
  };

  const toggleColorMode = () => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  };

  const handleAboutClick = (event: React.MouseEvent<HTMLElement>) => {
    setAboutAnchorEl(event.currentTarget);
  };

  const handleAboutClose = () => {
    setAboutAnchorEl(null);
  };

  const handleServicesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  const handleProductsClick = (event: React.MouseEvent<HTMLElement>) => {
    setProductsAnchorEl(event.currentTarget);
  };

  const handleProductsClose = () => {
    setProductsAnchorEl(null);
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const aboutSubItems = [
    { key: 'nav.company', path: '/about/company' },
    { key: 'nav.missionvision', path: '/about/mission-vision' },
    { key: 'nav.career', path: '/about/career' },
  ];

  const servicesSubItems = [
    { key: 'nav.services.software', path: '/services/software-development' },
    { key: 'nav.services.digital', path: '/services/digital-transformation' },
    { key: 'nav.services.consulting', path: '/services/it-consulting' },
    { key: 'nav.services.cloud', path: '/services/cloud-solutions' },
  ];

  const productsSubItems = [
    { key: 'products.verilobi.title', path: '/products/verilobi' },
    { key: 'products.beachbot.title', path: '/products/beachbot' },
  ];

  const navItems = [
    { key: 'nav.projects', path: '/', hash: '#projects' },
    { key: 'nav.blog', path: '/blog' },
    { key: 'nav.contact', path: '/', hash: '#contact' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isAboutActive = aboutSubItems.some(item => location.pathname === item.path);
  const isServicesActive = servicesSubItems.some(item => location.pathname === item.path);
  const isProductsActive = productsSubItems.some(item => location.pathname === item.path);

  return (
    <GlassAppBar position="fixed" elevation={trigger ? 4 : 0} component="header" role="banner">
      <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: { xs: 2, md: 3 } }}>
        <Box 
          component={Link} 
          to="/" 
          aria-label="Teknolobi Home" 
          sx={{ 
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            '& svg': {
              height: { xs: '42px', md: '62px' },
              width: 'auto',
            },
          }}
        >
          <Logo variant={logoVariant} height={62} />
        </Box>

        {/* Desktop Navigation */}
        <Stack 
          component="nav"
          role="navigation"
          aria-label="Main navigation"
          direction="row" 
          spacing={1} 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
          }}
        >
          <NavButton
            onClick={handleAboutClick}
            endIcon={<KeyboardArrowDownIcon />}
            active={isAboutActive ? 'true' : 'false'}
            aria-controls={aboutMenuOpen ? 'about-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={aboutMenuOpen ? 'true' : undefined}
          >
            {t('nav.about')}
          </NavButton>
          <StyledMenu
            id="about-menu"
            anchorEl={aboutAnchorEl}
            open={aboutMenuOpen}
            onClose={handleAboutClose}
            MenuListProps={{
              'aria-labelledby': 'about-button',
            }}
          >
            {aboutSubItems.map((item) => (
              <StyledMenuItem
                key={item.key}
                component={Link}
                to={item.path}
                onClick={() => {
                  handleAboutClose();
                  handleLinkClick();
                }}
              >
                {t(item.key)}
              </StyledMenuItem>
            ))}
          </StyledMenu>
          <NavButton
            onClick={handleServicesClick}
            endIcon={<KeyboardArrowDownIcon />}
            active={isServicesActive ? 'true' : 'false'}
            aria-controls={servicesMenuOpen ? 'services-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={servicesMenuOpen ? 'true' : undefined}
          >
            {t('nav.services')}
          </NavButton>
          <StyledMenu
            id="services-menu"
            anchorEl={servicesAnchorEl}
            open={servicesMenuOpen}
            onClose={handleServicesClose}
            MenuListProps={{
              'aria-labelledby': 'services-button',
            }}
          >
            {servicesSubItems.map((item) => (
              <StyledMenuItem
                key={item.key}
                component={Link}
                to={item.path}
                onClick={() => {
                  handleServicesClose();
                  handleLinkClick();
                }}
              >
                {t(item.key)}
              </StyledMenuItem>
            ))}
          </StyledMenu>
          <NavButton
            onClick={handleProductsClick}
            endIcon={<KeyboardArrowDownIcon />}
            active={isProductsActive ? 'true' : 'false'}
            aria-controls={productsMenuOpen ? 'products-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={productsMenuOpen ? 'true' : undefined}
          >
            {t('nav.products')}
          </NavButton>
          <StyledMenu
            id="products-menu"
            anchorEl={productsAnchorEl}
            open={productsMenuOpen}
            onClose={handleProductsClose}
            MenuListProps={{
              'aria-labelledby': 'products-button',
            }}
          >
            {productsSubItems.map((item) => (
              <StyledMenuItem
                key={item.key}
                component={Link}
                to={item.path}
                onClick={() => {
                  handleProductsClose();
                  handleLinkClick();
                }}
              >
                {t(item.key)}
              </StyledMenuItem>
            ))}
          </StyledMenu>
          {navItems.map((item) => (
            <NavButton
              key={item.key}
              component={Link}
              to={item.path}
              onClick={() => {
                handleLinkClick();
                if (item.hash) {
                  setTimeout(() => {
                    const element = document.querySelector(item.hash);
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }
              }}
              active={isActive(item.path) ? 'true' : 'false'}
            >
              {t(item.key)}
            </NavButton>
          ))}
        </Stack>

        {/* Right Side Controls */}
        <Stack direction="row" spacing={1} alignItems="center" role="group" aria-label="Site controls">
          <ContactButton
            startIcon={<PhoneIcon sx={{ fontSize: 18 }} />}
            href="https://calendly.com/teknolobi"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {t('nav.contactButton')}
          </ContactButton>
          
          {/* Desktop Language and Theme Controls */}
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Typography
              variant="body2"
              sx={{
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
              aria-hidden="true"
            >
              {language === 'tr' ? 'EN' : 'TR'}
            </Typography>
            <IconButton 
              onClick={toggleLanguage}
              aria-label={`Switch to ${language === 'tr' ? 'English' : 'Turkish'}`}
              sx={{ 
                color: 'text.primary',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              <LanguageIcon />
            </IconButton>
          </Stack>
          
          <IconButton 
            onClick={toggleColorMode}
            aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              color: 'text.primary',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'rotate(180deg)',
              },
            }}
          >
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Mobile Hamburger Menu */}
          <IconButton
            sx={{ display: { xs: 'flex', md: 'none' }, color: 'text.primary' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>
        </Stack>
      </Toolbar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: '400px' },
            maxWidth: '400px',
            background: theme.palette.mode === 'dark'
              ? 'rgba(15, 23, 42, 0.98)'
              : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '-8px 0 32px rgba(0, 0, 0, 0.5)'
              : '-8px 0 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <Logo variant={mode === 'dark' ? 'white' : 'colored'} height={40} />
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Menu Items */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
            <Stack spacing={0.5} sx={{ px: 2 }}>
              <ListItemButton
                onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                <ListItemText
                  primary={t('nav.about')}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                />
                {mobileAboutOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={mobileAboutOpen} timeout="auto" unmountOnExit>
                <Stack spacing={0.5} sx={{ pl: 2, py: 1 }}>
                  {aboutSubItems.map((item) => (
                    <Button
                      key={item.key}
                      component={Link}
                      to={item.path}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileAboutOpen(false);
                        handleLinkClick();
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        py: 1,
                        px: 2,
                        borderRadius: '10px',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.03)',
                          color: 'text.primary',
                        },
                      }}
                    >
                      {t(item.key)}
                    </Button>
                  ))}
                </Stack>
              </Collapse>

              <ListItemButton
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                <ListItemText
                  primary={t('nav.services')}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                />
                {mobileServicesOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                <Stack spacing={0.5} sx={{ pl: 2, py: 1 }}>
                  {servicesSubItems.map((item) => (
                    <Button
                      key={item.key}
                      component={Link}
                      to={item.path}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileServicesOpen(false);
                        handleLinkClick();
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        py: 1,
                        px: 2,
                        borderRadius: '10px',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.03)',
                          color: 'text.primary',
                        },
                      }}
                    >
                      {t(item.key)}
                    </Button>
                  ))}
                </Stack>
              </Collapse>

              <ListItemButton
                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              >
                <ListItemText
                  primary={t('nav.products')}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                />
                {mobileProductsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
                <Stack spacing={0.5} sx={{ pl: 2, py: 1 }}>
                  {productsSubItems.map((item) => (
                    <Button
                      key={item.key}
                      component={Link}
                      to={item.path}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileProductsOpen(false);
                        handleLinkClick();
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        py: 1,
                        px: 2,
                        borderRadius: '10px',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.03)',
                          color: 'text.primary',
                        },
                      }}
                    >
                      {t(item.key)}
                    </Button>
                  ))}
                </Stack>
              </Collapse>

              {navItems.map((item) => (
                <Button
                  key={item.key}
                  component={Link}
                  to={item.path}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLinkClick();
                    if (item.hash) {
                      setTimeout(() => {
                        const element = document.querySelector(item.hash);
                        element?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                    px: 2,
                    borderRadius: '12px',
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                >
                  {t(item.key)}
                </Button>
              ))}

              <ContactButton
                startIcon={<PhoneIcon sx={{ fontSize: 18 }} />}
                href="https://calendly.com/teknolobi"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 2 }}
              >
                {t('nav.contactButton')}
              </ContactButton>
            </Stack>
          </Box>

          {/* Drawer Footer - Controls */}
          <Box
            sx={{
              p: 2,
              borderTop: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              {/* Language Toggle */}
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={toggleLanguage}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                >
                  <LanguageIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {language === 'tr' ? 'EN' : 'TR'}
                </Typography>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Dark Mode Toggle */}
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={toggleColorMode}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    '&:hover': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                >
                  {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                </IconButton>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {mode === 'dark' 
                    ? (language === 'tr' ? 'Açık' : 'Light')
                    : (language === 'tr' ? 'Koyu' : 'Dark')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </GlassAppBar>
  );
}